import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { CustomCard, CardFooter, CardBody } from "../../../../components/CustomCard";
import CustomButton from "../../../../components/CustomButton";
import { Plus as PlusIcon } from "flowbite-react-icons/outline";
import InformationCircleIcon from "../../../../components/Icons/InformationCircleIcon";
import ListComponent from "../MeetingList/MeetingList";
import { Meeting } from "../../interfaces";
import { ClientLicenseInterface } from "../../../../interfaces/common";
import { getLanguage } from "../../../../lang/helpers";
import Api from "../../../../services/Api";
import { openMeeting } from "../../../../helpers/open_meeting";

interface NextMeetingCardProps {
  meetingData: Array<Meeting>;
  openCancelModal: (meeting: any) => void;
  license: ClientLicenseInterface | null;
}

const NextMeetingCard = ({
  meetingData = [],
  openCancelModal,
  license,
}: NextMeetingCardProps) => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language);
  const [isMeetingWithinTenMinutes, setIsMeetingWithinTenMinutes] =
    useState(false);

  useEffect(() => {
    const checkMeetingTime = () => {
      if (meetingData && meetingData[0]) {
        const meetingTime = meetingData[0].startDate;
        const now = new Date();
        const diff = meetingTime.getSeconds() - now.getSeconds();
        const diffMinutes = diff / 60;
        if (diffMinutes >= 0 && diffMinutes <= 10) {
          setIsMeetingWithinTenMinutes(true);
        } else {
          setIsMeetingWithinTenMinutes(false);
        }
      } else {
        setIsMeetingWithinTenMinutes(false);
      }
    };

    // Initial check
    checkMeetingTime();

    // Set interval to check every minute
    const interval = setInterval(() => {
      checkMeetingTime();
    }, 60000); // 60000 milliseconds = 1 minute

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [meetingData]);

  const renderBooking = () => {
    if (!license) return null;
    let unBookableMessage = "";
    if (license.unBookable) {
      if (license.unBookable === "bookable_after_first_meeting") {
        unBookableMessage =
          lang === "sv"
            ? "Du kan boka fler möten efter ditt första möte."
            : "You will be able to book more meetings after your first session.";
      } else if (license.unBookable === "no_remaining_meetings") {
        unBookableMessage =
          lang === "sv"
            ? "Du har bokat alla dina möten."
            : "You have booked all your meetings.";
      } else {
        unBookableMessage = "Cannot book more meetings: " + license.unBookable;
      }
    }

    if (unBookableMessage.length === 0) {
      return (
        <CustomButton
          leftIcon={<PlusIcon className="stroke-2" aria-hidden="true" />}
          buttonType="outlineWhiteBase"
          onClick={() => navigate("/videocoach/book-meeting")}
        >
          {t("book-more-meetings")}
        </CustomButton>
      );
    } else {
      return (
        <div className="flex border border-gray-500 bg-indigo-800 rounded-xl p-4">
          <InformationCircleIcon alt="info" />
          <div className="ml-4">{unBookableMessage}</div>
        </div>
      );
    }
  };

  const goToMeeting = (meetingId: string) => {
    if (api.apiToken) {
      openMeeting(meetingId, api.apiToken, "client");
    } else {
      alert("Error - missing api token");
    }
  };

  return (
    <CustomCard className="overflow-visible">
      <CardBody className="p-4 flex flex-col gap-4 overflow-visible">
        <div className="flex flex-col gap-2">
          <div className="z-title2">
            {t("next-meeting")}
          </div>
          <ListComponent
            meetingData={[meetingData[0]]}
            isPreviousMeetings={false}
            animate={true}
            onCancellation={openCancelModal}
          />
        </div>
        <CustomButton
          buttonType={
            isMeetingWithinTenMinutes ? "blueBase" : "outlineWhiteBase"
          }
          onClick={() => goToMeeting(meetingData[0].id)}
        >
          {t("to-meeting")}
        </CustomButton>
      </CardBody>
      <CardFooter>
        <div className="pb-4">
          <ListComponent
            meetingData={meetingData.slice(1)}
            animate={true}
            isPreviousMeetings={false}
            onCancellation={openCancelModal}
          />
        </div>
        {renderBooking()}
      </CardFooter>
    </CustomCard>
  );
};

export default NextMeetingCard;
